import { resolveAdfColorForPicker } from '../hub-brand-palette';
import type { HubBrandPalette, PaletteRef } from '../hub-brand-palette';

import { resolveAutoTextColor } from './resolve-auto-text-color';

export type HubAutoTextColorArgs = {
	/** The element's stored background — a hub var, a token, or a hex. */
	backgroundColor: string | undefined;
	/** Shade an *entitled* element falls back to when no background is stored. */
	defaultRef: PaletteRef;
	/** The element's stored text colour. */
	textColor: string | undefined;
};

/**
 * Palette-aware core of the auto-readability rule. Kept pure and separate from
 * the hook so elements that render a list of surfaces — the carousel maps over
 * frames, each with its own background — can call it inside a `map` where a
 * hook is not allowed.
 *
 * The background is resolved through the in-memory `palette`, never through
 * `getComputedStyle`. `extractColorCode` looks custom properties up on
 * `document.documentElement`, but `<HubThemingProvider>` emits the hub palette
 * on its own wrapper div — so a hub var resolves to `''` there, `tinycolor`
 * reads that as black, and the readability verdict inverts on every light
 * background.
 */
export const resolveHubAutoTextColor = ({
	backgroundColor,
	textColor,
	defaultRef,
	palette,
	primaryColor,
	isBrandPaletteLocked = false,
	packageName,
}: HubAutoTextColorArgs & {
	/**
	 * `true` for un-entitled (Free / Standard) tenants. They have no palette to
	 * spend, so `getHubDefaultParameters` gives *every* element the brand colour
	 * itself rather than a per-element shade — which changes what an unstored
	 * background resolves to here.
	 */
	isBrandPaletteLocked?: boolean;
	packageName: string;
	palette: HubBrandPalette;
	primaryColor: string;
}): string | undefined => {
	// An unstored background means the element is painting its default, and the
	// default differs by tier: Free/Standard renders `var(--hub-primary-color)`
	// (the brand colour) while Premium renders the element's palette shade. Using
	// the shade for a locked tenant would measure text against a light tint while
	// the element actually paints the full brand colour — inverting the result for
	// any dark brand. Mirrors `getHubDefaultParameters`'s locked branch.
	if (!backgroundColor) {
		return resolveAutoTextColor({
			textColor,
			backgroundHex: isBrandPaletteLocked ? primaryColor : palette[defaultRef],
			packageName,
		});
	}

	const backgroundHex = resolveAdfColorForPicker(
		backgroundColor,
		palette,
		defaultRef,
		primaryColor,
		primaryColor,
	);

	return resolveAutoTextColor({ textColor, backgroundHex, packageName });
};
